import { Injectable } from "@angular/core";
import { saveAs } from 'file-saver';

@Injectable({
    providedIn: 'root'
})
export class ServiceFactoryService {

    constructor() {}

    createInterfaceFromDefinitions(spec: any) {
        const modelDefinitions = Object.keys(spec.controller.definitions).reduce((acc, definition) => {
            const model = spec.controller.definitions[definition];

            const properties = [];
            if (model.properties) {
                properties.push(...Object.keys(model.properties).map(prop => {
                    const values = model.properties[prop];
                    let required = '?';
                    if (model?.required) {
                        required = model?.required.find(k => k === prop) ? '' : '?';
                    }
                    const propertyType = this.getPropertyType(values, spec.controller);
                    return `${prop}${required}: ${propertyType};`;
                }));
            }
            acc.push({
                interfaceName: definition.replace(/\./g, ''),
                properties
            });
            return acc;
          }, []);

        console.log(modelDefinitions);

        const generatedCode = modelDefinitions.map(({interfaceName, properties}) => {
            const newInterface = `
                export interface ${interfaceName} {
                    ${properties.join('\n\t\t\t\t\t')}
                }
            `;
            return newInterface;
        }).join('\n');

        // Create a Blob object representing the file
        const blob = new Blob([generatedCode], { type: 'text/plain;charset=utf-8' });

        // Save the file with the desired filename
        saveAs(blob, `${spec.controllerName}.models.ts`);
    }

    createServiceFromPath(spec: any) {
        const controllers = Object.keys(spec.controller.paths).reduce((acc, path) => {
            const methods = spec.controller.paths[path];
            Object.keys(methods).forEach((method) => {
              acc.push({
                path: path.replace('/api/', ''),
                method,
                functionName: this.convertPathToFunction(path, method),
                parameters: methods[method].parameters,
                summary: methods[method]?.summary
              });
            });
            return acc;
          }, []);

          const generatedCode = controllers
            .map(({ path, method, functionName, parameters, summary }) => {
                const functionParams = parameters.filter(p => p.in === 'path').map(p => {
                    return `${p.name}${!p.required ? '?' : ''}: ${this.getParameterType(p.type)}`;
                });
                const queryParams = parameters.filter(p => p.in === 'query').map(p => {
                    return `${p.name}${!p.required ? '?' : ''}: ${this.getParameterType(p.type)}`;
                });

                const bodyParams = parameters.filter(p => p.in === 'body').map(p => {
                    return `${p.name}${!p.required ? '?' : ''}: ${this.getBodyParameterType(p, spec.controller)}`;
                });

                const passedBodyParams = parameters.filter(p => p.in === 'body').map(p => {
                    return `${p.name}`;
                });

                parameters.filter(p => p.in === 'path').forEach(segment => {
                    path = path.replace(`{${segment.name}}`, `$\{${segment.name}\}`);
                });

                if (bodyParams && bodyParams.length) {
                    functionParams.push(... bodyParams);
                    return `
                    /**
                     * ${summary}
                     */
                    ${functionName}(${functionParams.join(', ')}) {
                        return this.${method}(\`${path}\`, ${passedBodyParams.join(', ')});
                    }`;
                } else {
                    return `
                    /**
                     * ${summary}
                     */
                    ${functionName}(${functionParams.join(', ')}) {
                        return this.${method}(\`${path}\`);
                    }`;
                }

            })
            .join('\n');

            const modelDefinitions = Object.keys(spec.controller.definitions).reduce((acc, definition) => {
                const model = spec.controller.definitions[definition];
    
                const properties = [];
                if (model.properties) {
                    properties.push(...Object.keys(model.properties).map(prop => {
                        const values = model.properties[prop];
                        let required = '?';
                        if (model?.required) {
                            required = model?.required.find(k => k === prop) ? '' : '?';
                        }
                        const propertyType = this.getPropertyType(values, spec.controller);
                        return `${prop}${required}: ${propertyType};`;
                    }));
                }
                acc.push(definition.replace(/\./g, ''));
                return acc;
              }, []);

        const serviceStart = `
            import { Injectable } from '@angular/core';
            import { RockService } from './rock.service';
            import { ${modelDefinitions.join(', ')}} from './Models/${spec.controllerName}.models';

            @Injectable()
            export class ${spec.controllerName}Service extends RockService {
            
                override entityType = '${spec.controllerName}';
            `;
        
        const serviceEnd = `\n\n}`

        // Create a Blob object representing the file
        const blob = new Blob([serviceStart,generatedCode, serviceEnd], { type: 'text/plain;charset=utf-8' });

        // Save the file with the desired filename
        saveAs(blob, `${spec.controllerName}.service.ts`);
    }

    convertPathToFunction(path: string, method: string) {
        let functionName = path.replace('/api/', '');

        functionName = functionName.replace(/\//g, '');
        functionName = functionName.replace(/\{/g, 'By');
        functionName = functionName.replace(/\}/g, '');

        const methodName = method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
        
        return methodName + functionName;
    }

    getPropertyType(ref: any, spec: any) {

        if (ref?.type === 'array') {
            if (ref.items?.type) {
                return this.getDefinition(ref.items.type, spec, true);
            }
            if (ref?.items?.$ref) {
                return this.getDefinition(ref.items.$ref, spec, true);
            }
        } 

        if (ref?.additionalProperties?.$ref) {
            return this.getDefinition(ref.additionalProperties.$ref, spec);
        }

        if (ref.$ref) {
            return this.getDefinition(ref.$ref, spec);
        }

        if (ref?.type) {
            return this.getParameterType(ref.type);
        }
    }

    getParameterType(type: string) {
        switch (type) {
            case 'integer':
                return 'number'
            case 'string':
            default:
                return type;
        }
    }

    getBodyParameterType(parameter: any, spec: any) {

        if (parameter?.type) {
            return parameter.type;
        }

        if (parameter?.schema?.type) {
            return parameter.schema.type;
        }

        if (parameter?.schema?.$ref) {
            return this.getDefinition(parameter.schema.$ref, spec)
        }
    }

    getDefinition(ref: any, spec: any, array: boolean = false) {
        const entityType = ref.replace('#/definitions/', '');
        const definition = Object.keys(spec.definitions).find(k => k === entityType);

        if (array) {
            return definition ? `${definition.replace(/\./g, '')}[]` : 'any';
        }

        return definition ? definition.replace(/\./g, '') : 'any';
    }

}